// Rascunho do fluxo sem React: o logout (src/app) importa só este arquivo (`clearFlowDrafts`), sem
// puxar a tela do fluxo para o bundle inicial.

/** O que o rascunho guarda: valores declarados e passos concluídos. */
export interface FlowDraftState {
  readonly values: Readonly<Record<string, unknown>>;
  readonly completed: readonly string[];
}

/** Onde o fluxo guarda o rascunho entre recargas (F5). */
export interface FlowDraft {
  readonly read: () => FlowDraftState | undefined;
  readonly write: (state: FlowDraftState) => void;
  readonly clear: () => void;
}

/** Prefixo de toda chave de rascunho no `sessionStorage`: `uyzy.flow.<fluxo>.<tenant>.<sub>`. */
export const FLOW_DRAFT_PREFIX = 'uyzy.flow.';

function sessionStore(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

/** A chave do rascunho, por fluxo, correspondente e usuário; sem os dois últimos, não há rascunho. */
export function flowDraftKey(flowId: string, tenant: string | undefined, user: string | undefined): string | undefined {
  if (!tenant || !user) return undefined;
  return `${FLOW_DRAFT_PREFIX}${flowId}.${tenant}.${user}`;
}

const normalize = (segment: string) =>
  segment
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

// Nomes que nunca vão ao rascunho, mesmo declarados (B1 da revisão do !11135): senha, código/TOTP e
// documento. `registrationNumber` é o CPF/CNPJ no legado (UpdateConsignmentForm.tsx:22).
const SENSITIVE_EXACT = new Set([
  'code',
  'codigo',
  'otp',
  'pin',
  'mfa',
  'document',
  'documento',
  'documents',
  'documentos',
  'rg',
  'taxid',
  'taxpayerid',
]);
const SENSITIVE_PART = [
  'password',
  'passwd',
  'passphrase',
  'senha',
  'secret',
  'segredo',
  'token',
  'totp',
  'mfacode',
  'otpcode',
  'smscode',
  'authcode',
  'onetimecode',
  'verificationcode',
  'confirmationcode',
  'codigoverificacao',
  'codigoconfirmacao',
  'cpf',
  'cnpj',
  'registrationnumber',
  'documentnumber',
  'numerodocumento',
  'taxidnumber',
  'rgnumber',
  'pwd',
  'credential',
  'apikey',
  'cvv',
  'cardnumber',
];

function isSensitiveSegment(segment: string): boolean {
  const name = normalize(segment);
  return SENSITIVE_EXACT.has(name) || name.endsWith('otp') || SENSITIVE_PART.some((part) => name.includes(part));
}

/** Caminho que o rascunho recusa sempre: algum trecho é senha, código/TOTP ou documento (CPF/CNPJ). */
export function isSensitiveDraftPath(path: string): boolean {
  return path.split('.').some(isSensitiveSegment);
}

// Arquivo nunca vai ao storage (não serializa e pode ser documento digitalizado).
function isBinary(value: unknown): boolean {
  return (typeof Blob !== 'undefined' && value instanceof Blob) || (typeof FileList !== 'undefined' && value instanceof FileList);
}

const SKIP = Symbol('skip');

function sanitize(value: unknown): unknown {
  if (isBinary(value)) return SKIP;
  if (Array.isArray(value)) return value.map((item) => sanitize(item)).filter((item) => item !== SKIP);
  if (value !== null && typeof value === 'object') {
    if (value instanceof Date) return value;
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      if (isSensitiveSegment(key)) continue;
      const clean = sanitize(item);
      if (clean !== SKIP) out[key] = clean;
    }
    return out;
  }
  if (typeof value === 'function' || typeof value === 'symbol') return SKIP;
  return value;
}

function readPath(source: unknown, segments: readonly string[]): { readonly found: boolean; readonly value?: unknown } {
  let cursor: unknown = source;
  for (const segment of segments) {
    if (cursor === null || typeof cursor !== 'object' || !Object.hasOwn(cursor, segment)) return { found: false };
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return { found: true, value: cursor };
}

function writePath(target: Record<string, unknown>, segments: readonly string[], value: unknown) {
  let cursor = target;
  segments.slice(0, -1).forEach((segment) => {
    const next = cursor[segment];
    if (next === null || typeof next !== 'object' || Array.isArray(next)) cursor[segment] = {};
    cursor = cursor[segment] as Record<string, unknown>;
  });
  const last = segments.at(-1);
  if (last !== undefined) cursor[last] = value;
}

/**
 * O que vai ao rascunho: só os caminhos declarados (`a.b.c`), sem nenhum trecho sensível dentro deles
 * (senha, código/TOTP, documento, arquivo). Nada declarado, nada gravado.
 */
export function draftSnapshot(values: unknown, include: readonly string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const path of include) {
    if (path === '' || isSensitiveDraftPath(path)) continue;
    const segments = path.split('.');
    const { found, value } = readPath(values, segments);
    if (!found) continue;
    const clean = sanitize(value);
    if (clean !== SKIP) writePath(out, segments, clean);
  }
  return out;
}

/** Junta o rascunho aos valores do formulário por caminho (objetos por dentro; lista e valor trocam). */
export function mergeDraft<T>(base: T, draft: Readonly<Record<string, unknown>>): T {
  const merge = (left: unknown, right: unknown): unknown => {
    if (right === null || typeof right !== 'object' || Array.isArray(right) || right instanceof Date) return right;
    const from = left !== null && typeof left === 'object' && !Array.isArray(left) ? (left as Record<string, unknown>) : {};
    const out: Record<string, unknown> = { ...from };
    for (const [key, value] of Object.entries(right)) out[key] = merge(from[key], value);
    return out;
  };
  return merge(base, draft) as T;
}

/** Um rascunho numa chave, com a lista de permissão aplicada ao gravar e ao ler. */
export function createFlowDraft(key: string, include: readonly string[], storage: () => Storage | undefined = sessionStore): FlowDraft {
  return {
    read: () => {
      try {
        const raw = storage()?.getItem(key);
        if (!raw) return undefined;
        const parsed = JSON.parse(raw) as Partial<FlowDraftState> | null;
        if (!parsed || typeof parsed.values !== 'object' || !Array.isArray(parsed.completed)) return undefined;
        return { values: draftSnapshot(parsed.values, include), completed: parsed.completed.filter((id): id is string => typeof id === 'string') };
      } catch {
        return undefined;
      }
    },
    write: (state) => {
      try {
        storage()?.setItem(key, JSON.stringify({ values: draftSnapshot(state.values, include), completed: state.completed }));
      } catch {
        // Sem storage (aba privada, cota): o fluxo segue, só não sobrevive ao F5.
      }
    },
    clear: () => {
      try {
        storage()?.removeItem(key);
      } catch {
        // idem
      }
    },
  };
}

/** Apaga todos os rascunhos de fluxo da aba (`uyzy.flow.*`). A casca chama no logout. */
export function clearFlowDrafts(storage: Storage | undefined = sessionStore()): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(FLOW_DRAFT_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // Sem storage: não há o que limpar.
  }
}
